import React, { useState, useEffect } from 'react';
import { fetchDomains, fetchUseCases, /* fetchTools, */ fetchAgents, fetchActiveAgents, selectAgent, deactivateAgent, deleteAgent } from './api';
import AgentCard from './components/AgentCard';
import UseCaseCard from './components/UseCaseCard';
import RegisterAgentModal from './components/RegisterAgentModal';
import { useNavigate } from 'react-router-dom';
import { CUSTOM_USE_CASE_ROUTES } from '../chatbotScreen/constants';
import { isAdmin } from '../../services/authService';
import { toast } from 'react-toastify';

const AgentMarketplace = () => {
    const [domains, setDomains] = useState([]);
    const [useCases, setUseCases] = useState([]);
    const [tools, setTools] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedDomain, setSelectedDomain] = useState('all');
    const [activeTab, setActiveTab] = useState('tools');
    const [searchQuery, setSearchQuery] = useState('');
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [activeAgentIds, setActiveAgentIds] = useState(new Set());
    const [showActiveOnly, setShowActiveOnly] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchMarketplaceData = async () => {
            try {
                setLoading(true);

                const [domainsData, useCasesData, agentsData] = await Promise.all([
                    fetchDomains(),
                    fetchUseCases(),
                    // fetchTools(),
                    fetchAgents()
                ]);

                setDomains(domainsData);
                setUseCases(useCasesData);
                // setTools(toolsData);
                setAgents(agentsData);

                const userId = sessionStorage.getItem('user_id');
                if (userId) {
                    const active = await fetchActiveAgents(userId);
                    setActiveAgentIds(new Set(active.map(a => a.agent_version_id ?? a.agent_id ?? a.id)));
                }

                setLoading(false);
            } catch (err) {
                console.error('Error fetching marketplace data:', err);
                setError('Failed to load marketplace data. Please try again later.');
                setLoading(false);
            }
        };

        fetchMarketplaceData();
    }, []);

    const filteredUseCases = useCases.filter(useCase => {
        const matchesDomain = selectedDomain === 'all' || useCase.domain_id === selectedDomain;
        const matchesSearch = useCase.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (useCase.description && useCase.description.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesDomain && matchesSearch;
    });

    const filteredAgents = agents.filter(agent => {
        const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (agent.description && agent.description.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesActive = !showActiveOnly || activeAgentIds.has(agent.agent_version_id);
        return matchesSearch && matchesActive;
    });

    const handleUseAgent = async (agent) => {
        const userId = sessionStorage.getItem('user_id');
        if (!userId) return;
        try {
            await selectAgent(userId, agent.agent_version_id);
            setActiveAgentIds(prev => new Set([...prev, agent.agent_version_id]));
            toast.success(`${agent.name} activated successfully`);
        } catch (err) {
            toast.error(`Failed to activate agent: ${err.message}`);
        }
    };

    const handleDeactivateAgent = async (agent) => {
        const userId = sessionStorage.getItem('user_id');
        if (!userId) return;
        try {
            await deactivateAgent(userId, agent.agent_version_id);
            setActiveAgentIds(prev => {
                const next = new Set(prev);
                next.delete(agent.agent_version_id);
                return next;
            });
            toast.success(`${agent.name} deactivated successfully`);
        } catch (err) {
            toast.error(`Failed to deactivate agent: ${err.message}`);
        }
    };

    const handleItemSelect = (type, item) => {
        if (type === 'use-case') {
            if (item.name === 'Invest AI') {
                navigate('/user-dashboard');
                return;
            }
            const customRoute = CUSTOM_USE_CASE_ROUTES[item.name];
            if (customRoute) {
                if (customRoute.startsWith('http')) {
                    window.location.href = customRoute;
                } else {
                    navigate(customRoute);
                }
            } else if (item.slug) {
                navigate(`/use-case/${item.slug}`);
            } else {
                navigate(`/use-case/${item.id}`);
            }
        } else if (type === 'agent') {
            navigate(`/agent/${item.agent_id}`);
        }
    };

    const getDomainName = (domainId) => {
        const domain = domains.find(d => d.id === domainId);
        return domain ? domain.name : 'Unknown Domain';
    };

    const getDomainColor = (domainId) => {
        const colors = [
            'bg-blue-100 text-primaryDark',
            'bg-green-100 text-green-800',
            'bg-purple-100 text-purple-800',
            'bg-yellow-100 text-yellow-800',
            'bg-pink-100 text-pink-800',
            'bg-indigo-100 text-indigo-800'
        ];
        const colorIndex = parseInt(domainId, 10) % colors.length;
        return colors[colorIndex] || colors[0];
    };

    const handleDeleteAgent = async (agent) => {
        if (!window.confirm(`Are you sure you want to delete ${agent.name}?`)) return false;
        try {
            await deleteAgent(agent.agent_id);
            setAgents(prevAgents => prevAgents.filter(a => a.agent_id !== agent.agent_id));
            toast.success(`${agent.name} deleted successfully`);
            return true;
        } catch (error) {
            console.error('Error deleting agent:', error);
            toast.error(`Failed to delete agent: ${error.message}`);
            return false;
        }
    };

    const TABS = [
        { id: 'tools', label: 'Tools & Agents' },
        { id: 'use-cases', label: 'Use Cases' },
    ];

    return (
        <div className="h-full flex flex-col bg-gray-100">
            {/* Header */}
            <div className="flex-none bg-gray-100 px-6 shadow-sm z-20">
                {/* Title row */}
                <div className="pt-4 pb-3 border-b border-gray-300">
                    <h1 className="text-2xl font-bold text-primary">Nova Marketplace</h1>
                </div>

                {/* Tabs */}
                <nav className="-mb-px flex gap-6">
                    {TABS.map(({ id, label }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                                activeTab === id
                                    ? 'border-secondary text-secondary font-semibold'
                                    : 'border-transparent text-gray-600 hover:text-primaryDark hover:border-gray-300'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-6">
                {loading && (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                )}

                {error && !loading && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-8" role="alert">
                        <span className="block sm:inline">{error}</span>
                    </div>
                )}

                {/* Search + domain + add button */}
                {!loading && !error && (
                    <div className="flex items-center gap-2 mb-5">
                        <div className="relative flex-1 max-w-xs">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                                <svg className="w-3.5 h-3.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                                </svg>
                            </div>
                            <input
                                type="search"
                                placeholder="Search..."
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="relative w-36">
                            <select
                                className="w-full px-2.5 py-1.5 pr-7 border border-gray-300 rounded-lg bg-white text-gray-700 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors appearance-none cursor-pointer"
                                value={selectedDomain}
                                onChange={(e) => setSelectedDomain(e.target.value)}
                            >
                                <option value="all">All Domains</option>
                                {domains.map(domain => (
                                    <option key={domain.id} value={domain.id}>{domain.name}</option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {activeTab === 'tools' && (
                            <button
                                onClick={() => setShowActiveOnly(prev => !prev)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors duration-200 whitespace-nowrap ${
                                    showActiveOnly
                                        ? 'bg-green-100 text-green-700 border-green-400'
                                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                                Active only
                            </button>
                        )}
                        <div className="flex-1" />
                        {isAdmin() && (
                            <button
                                onClick={() => setShowRegisterModal(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primaryDark transition-colors duration-200 whitespace-nowrap shadow-sm"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add New Agent
                            </button>
                        )}
                    </div>
                )}

                {!loading && !error && (
                    <div className="mx-auto">
                        {activeTab === 'tools' && (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 animate-fadeIn">
                                {filteredAgents.length > 0 ? (
                                    filteredAgents.map((agent, index) => (
                                        <div key={agent.agent_id} style={{ animationDelay: `${index * 50}ms` }}>
                                            <AgentCard
                                                agent={agent}
                                                onSelect={handleItemSelect}
                                                domainName={agent.publisher || agent.agent_type || 'Agent'}
                                                domainColor="bg-blue-100 text-primaryDark"
                                                isActive={activeAgentIds.has(agent.agent_version_id)}
                                                onUse={handleUseAgent}
                                                onDeactivate={handleDeactivateAgent}
                                                onEdit={isAdmin() ? setEditingAgent : undefined}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-full text-center py-16">
                                        <div className="inline-block p-4 bg-gray-100 rounded-full mb-4">
                                            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <p className="text-gray-500 text-lg">No agents found matching your criteria.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'use-cases' && (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 animate-fadeIn">
                                {filteredUseCases.length > 0 ? (
                                    filteredUseCases.map((useCase, index) => (
                                        <div key={useCase.id} style={{ animationDelay: `${index * 50}ms` }}>
                                            <UseCaseCard
                                                useCase={useCase}
                                                onSelect={handleItemSelect}
                                                domainName={getDomainName(useCase.domain_id)}
                                                domainColor={getDomainColor(useCase.domain_id)}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-full text-center py-16">
                                        <div className="inline-block p-4 bg-gray-100 rounded-full mb-4">
                                            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                            </svg>
                                        </div>
                                        <p className="text-gray-500 text-lg">No use cases found matching your criteria.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showRegisterModal && (
                <RegisterAgentModal
                    onClose={() => setShowRegisterModal(false)}
                    onSuccess={() => {
                        setShowRegisterModal(false);
                        window.location.reload();
                    }}
                />
            )}
            {editingAgent && (
                <RegisterAgentModal
                    agent={editingAgent}
                    onClose={() => setEditingAgent(null)}
                    onDelete={handleDeleteAgent}
                    onSuccess={() => {
                        setEditingAgent(null);
                        window.location.reload();
                    }}
                />
            )}
        </div>
    );
};

export default AgentMarketplace;
